import React, { useState, useEffect } from 'react';
import { Gamepad2 } from 'lucide-react';
import { Wallet, WalletTransaction, WalletTransactionFilters } from '../../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { formatCurrency, cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { NumberedPagination, SortableTableHead } from '@kioskgaming/ui';
import { useWalletTransactions } from '../../../hooks/useApi';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import {
  AdminTableColgroup,
  ADMIN_RAW_CELL,
  ADMIN_TABLE_FIXED_CLASS,
  adminCellClass,
  PLAYER_WALLET_TX_COLUMNS,
  WT,
} from '../../../config/adminTableLayout';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';

const TRANSACTION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'credit', label: 'Credit' },
  { value: 'debit', label: 'Debit' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'adjustment', label: 'Balance correction' },
] as const;

interface WalletTransactionTableProps {
  userId: string;
  wallet?: Wallet;
}

const WalletTransactionTable: React.FC<WalletTransactionTableProps> = ({ userId, wallet }) => {
  const fmt = useFormatDate();
  const [filters, setFilters] = useState<WalletTransactionFilters>({
    page: 1,
    limit: 20,
    type: '',
    dateFrom: '',
    dateTo: '',
  });

  const tableSort = useTableSort({
    columns: getTableSortColumns('playerWalletTransactions'),
    paramPrefix: 'walletTx',
    onChange: () => {
      setFilters((prev) => ({ ...prev, page: 1 }));
    },
  });

  useEffect(() => {
    setFilters((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: WalletTransactionFilters = { ...prev, page: 1 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as WalletTransactionFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder]);

  const { data: transactionsData, isLoading, isFetching, error, refetch } = useWalletTransactions(userId, filters);

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, dateFrom: e.target.value, page: 1 }));
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, dateTo: e.target.value, page: 1 }));
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters(prev => ({
      ...prev,
      type: e.target.value as WalletTransactionFilters['type'],
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleClearFilters = () => {
    tableSort.resetSort();
    setFilters({
      page: 1,
      limit: 20,
      type: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load wallet transactions</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const transactions = transactionsData?.data?.transactions || [];
  const pagination = transactionsData?.data?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  };

  const headerSort = tableSort.sortBy
    ? {
        activeKey: pagination.sortBy ?? tableSort.sortBy,
        direction: (pagination.sortOrder ?? tableSort.sortOrder) as SortDirection | null,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = isFetching && transactions.length > 0;

  /**
   * Debit = money out of wallet (minus, red): withdrawal, or "multi-game deposit" (wallet → game).
   * Credit = money into wallet (plus, green): deposit from outside.
   */
  const isDebit = (transaction: any) => {
    const t = (transaction.type || '').toLowerCase();
    if (t === 'debit' || t === 'withdrawal') return true;
    const desc = (transaction.description || '').toLowerCase();
    const ref = (transaction.referenceType || '').toLowerCase();
    const text = `${desc} ${ref}`;
    // Multi-game deposit = wallet → game (money leaves wallet) → show as debit
    if (text.includes('multi-game deposit') || text.includes('multi game deposit')) return true;
    return false;
  };

  const getAmountDisplay = (transaction: any) => {
    const rawAmount = transaction.amount;
    const amount = rawAmount != null && !isNaN(parseFloat(String(rawAmount)))
      ? parseFloat(String(rawAmount))
      : null;
    if (amount === null) return '-';
    const formatted = formatCurrency(amount);
    return isDebit(transaction) ? `-${formatted}` : `+${formatted}`;
  };

  const getAmountColor = (transaction: any) =>
    isDebit(transaction) ? 'text-red-600' : 'text-green-600';

  const getBalanceDisplay = (transaction: any) => {
    const before = transaction.balanceBefore;
    const after = transaction.balanceAfter;
    const numBefore = typeof before === 'number' ? before : (before != null ? parseFloat(before) : NaN);
    const numAfter = typeof after === 'number' ? after : (after != null ? parseFloat(after) : NaN);
    const hasBefore = !isNaN(numBefore);
    const hasAfter = !isNaN(numAfter);
    if (hasBefore && hasAfter) {
      return `${formatCurrency(numBefore)} → ${formatCurrency(numAfter)}`;
    }
    if (hasAfter) return formatCurrency(numAfter);
    if (transaction.balance != null) return formatCurrency(parseFloat(String(transaction.balance)));
    return '-';
  };

  const allocations = (transaction: any): Array<{ gameName: string; amount: number }> => {
    const list = transaction.metadata?.allocations;
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.map((a: any) => ({
      gameName: a.gameProviderName ?? a.gameName ?? a.gameProviderCode ?? a.providerCode ?? 'N/A',
      amount: typeof a.amount === 'number' ? a.amount : parseFloat(a.amount) || 0,
    }));
  };

  const getTypeBadge = (type: string) => {
    const config: Record<string, { className: string; label: string }> = {
      deposit: { className: 'bg-green-100 text-green-700', label: 'Deposit' },
      withdrawal: { className: 'bg-orange-100 text-orange-700', label: 'Withdrawal' },
      credit: { className: 'bg-emerald-100 text-emerald-700', label: 'Credit' },
      debit: { className: 'bg-red-100 text-red-700', label: 'Debit' },
      transfer: { className: 'bg-blue-100 text-blue-700', label: 'Transfer' },
      bonus: { className: 'bg-purple-100 text-purple-700', label: 'Bonus' },
      adjustment: { className: 'bg-gray-100 text-gray-700', label: 'Balance correction' },
    };
    const c = config[type.toLowerCase()] || { className: 'bg-gray-100 text-gray-700', label: type };
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', c.className)}>
        {c.label}
      </span>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Wallet Transactions</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium whitespace-nowrap">Type:</label>
            <select
              value={filters.type || ''}
              onChange={handleTypeChange}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {TRANSACTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2 flex-1">
            <label className="text-sm font-medium whitespace-nowrap">From Date:</label>
            <Input
              type="date"
              value={filters.dateFrom || ''}
              onChange={handleDateFromChange}
              className="max-w-xs"
            />
          </div>
          <div className="flex items-center space-x-2 flex-1">
            <label className="text-sm font-medium whitespace-nowrap">To Date:</label>
            <Input
              type="date"
              value={filters.dateTo || ''}
              onChange={handleDateToChange}
              className="max-w-xs"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </div>

        {/* Transaction Table */}
        {isLoading && transactions.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <p className="text-center text-muted-foreground">No transactions found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className={ADMIN_TABLE_FIXED_CLASS} aria-busy={isTableBusy || undefined}>
                  <AdminTableColgroup columns={PLAYER_WALLET_TX_COLUMNS} />
                  <thead>
                    <tr className="border-b">
                      <SortableTableHead
                        sortable
                        className={ADMIN_RAW_CELL}
                        label="Date"
                        sortKey="created_at"
                        activeKey={headerSort.activeKey}
                        direction={headerSort.direction}
                        onSort={tableSort.toggleSort}
                        disabled={isTableBusy}
                      />
                      <SortableTableHead
                        sortable
                        className={ADMIN_RAW_CELL}
                        label="Type"
                        sortKey="type"
                        activeKey={headerSort.activeKey}
                        direction={headerSort.direction}
                        onSort={tableSort.toggleSort}
                        disabled={isTableBusy}
                      />
                      <th className={cn(ADMIN_RAW_CELL, 'text-left font-medium')}>Description</th>
                      <SortableTableHead
                        sortable
                        className={cn(ADMIN_RAW_CELL, adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.amount]))}
                        label="Amount"
                        sortKey="amount"
                        activeKey={headerSort.activeKey}
                        direction={headerSort.direction}
                        onSort={tableSort.toggleSort}
                        align="right"
                        magnitude
                        disabled={isTableBusy}
                      />
                      <th className={cn(ADMIN_RAW_CELL, 'text-right font-medium whitespace-nowrap', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.balance]))}>Balance (Before → After)</th>
                      <th className={cn(ADMIN_RAW_CELL, 'text-left font-medium')}>Reference</th>
                    </tr>
                  </thead>
                  <tbody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b hover:bg-accent/50">
                        <td className={cn(ADMIN_RAW_CELL, 'text-sm', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.date]))}>
                          {fmt(transaction.created_at ?? transaction.createdAt)}
                        </td>
                        <td className={cn(ADMIN_RAW_CELL, 'text-sm', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.type]))}>
                          {getTypeBadge(transaction.type)}
                        </td>
                        <td className={cn(ADMIN_RAW_CELL, 'text-sm align-top min-w-0', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.description]))}>
                          <div>
                            <span>{transaction.description}</span>
                            {(() => {
                              const list = allocations(transaction);
                              if (list.length === 0) return null;
                              return (
                                <div className="mt-1.5 pl-0 space-y-0.5">
                                  {list.map((a, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <Gamepad2 className="h-3 w-3 shrink-0" />
                                      <span>{a.gameName}:</span>
                                      <span className="font-medium text-green-600">{formatCurrency(a.amount)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className={cn(ADMIN_RAW_CELL, 'text-sm font-medium', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.amount]), getAmountColor(transaction))}>
                          {getAmountDisplay(transaction)}
                        </td>
                        <td className={cn(ADMIN_RAW_CELL, 'text-sm font-mono whitespace-nowrap', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.balance]))}>
                          {getBalanceDisplay(transaction)}
                        </td>
                        <td className={cn(ADMIN_RAW_CELL, 'text-sm', adminCellClass(PLAYER_WALLET_TX_COLUMNS[WT.reference]))}>
                          {transaction.referenceId ? (
                            <span className="font-mono text-xs">{transaction.referenceId}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transactions
            </p>
            <NumberedPagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
              disabled={isLoading || isTableBusy}
              variant="admin"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WalletTransactionTable;
